import React, { useState } from 'react';
import { Upload, CheckCircle, ArrowRight, HelpCircle, ExternalLink, X } from 'lucide-react';

const SolutionUploadComponent = ({
  uploadHover,
  uploadStatus,
  isUploading,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleProcessSolutions,
  handleFileChange,
  showFileRestrictions,
  onShowExamples,
  onShowEssayGuidance
}) => {
  // State to track icon hover independently from drag hover
  const [iconHover, setIconHover] = useState(false);

  return (
    <div
      className="upload-section"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        margin: '0 auto',
        padding: '40px 0',
        height: '100%',
        position: 'relative',
        minHeight: '400px'
      }}
    >
      {/* Main Container - Side by side layout */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '30px',
          width: '100%',
          maxWidth: '1000px',
          margin: '10 auto',
          alignItems: 'flex-start'
        }}
      >
        {/* Left Side: Instructions Steps */}
        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid rgba(228, 177, 122, 0.12)',
            borderRadius: '16px',
            padding: '18px',
            boxShadow:
              'rgba(228, 177, 122, 0.06) 0px 2px 4px -2px, rgba(228, 177, 122, 0.04) 0px 4px 8px -2px',
            minHeight: '320px',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'default',
            userSelect: 'text'
          }}
        >
          {/* Header with buttons */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              marginBottom: '16px'
            }}
          >
            <h3
              style={{
                fontSize: '1.2rem',
                margin: '0',
                color: '#6b4a2a', // darken($text-primary, 15%)
                fontWeight: '600',
                textAlign: 'left'
              }}
            >
              Create Your Answer Key
            </h3>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  background: 'rgba(228, 177, 122, 0.1)',
                  border: '1px solid rgba(228, 177, 122, 0.2)',
                  borderRadius: '8px',
                  color: '#8c6b4a', // $text-primary
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  flexGrow: 1,
                  justifyContent: 'center',
                  minWidth: '140px',
                  textAlign: 'left'
                }}
                onClick={onShowEssayGuidance}
                onMouseOver={e => {
                  e.target.style.background = 'rgba(228, 177, 122, 0.15)';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 6px 7px rgba(228, 177, 122, 0.6)';
                }}
                onMouseOut={e => {
                  e.target.style.background = 'rgba(228, 177, 122, 0.1)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <HelpCircle size={16} />
                Grading Essays
              </button>
              {onShowExamples && (
                <button
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    background: 'rgba(228, 177, 122, 0.1)',
                    border: '1px solid rgba(228, 177, 122, 0.2)',
                    borderRadius: '8px',
                    color: '#8c6b4a', // $text-primary
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 0 10px rgba(228, 177, 122, 0.7)',
                    animation: 'subtle-pulse 2.5s infinite',
                    flexGrow: 1,
                    justifyContent: 'center',
                    minWidth: '140px',
                    textAlign: 'left'
                  }}
                  onClick={onShowExamples}
                  onMouseOver={e => {
                    e.target.style.background = 'rgba(228, 177, 122, 0.15)';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 6px 7px rgba(228, 177, 122, 0.6)';
                  }}
                  onMouseOut={e => {
                    e.target.style.background = 'rgba(228, 177, 122, 0.1)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 0 15px rgba(228, 177, 122, 0.7)';
                  }}
                >
                  <HelpCircle size={16} />
                  Worksheet Examples
                </button>
              )}
            </div>
          </div>

          {/* Steps */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              flex: 1
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: '16px',
                alignItems: 'flex-start'
              }}
            >
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #e4b17a, #c99b69)', // $primary-gradient
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '600',
                  fontSize: '1.1rem',
                  flexShrink: 0,
                  boxShadow: '0 2px 8px rgba(228, 177, 122, 0.2)'
                }}
              >
                1
              </div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <h4
                  style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#7a5d42',
                    margin: '0 0 2px 0',
                    textAlign: 'left',
                    display: 'inline-block'
                  }}
                >
                  Print a copy of your worksheet/test/lab
                </h4>
                <p
                  style={{
                    color: '#8f7752', // darken($text-secondary, 10%)
                    fontSize: '0.95rem',
                    margin: '0',
                    lineHeight: '1.4',
                    textAlign: 'left'
                  }}
                >
                  Get a blank copy of the assignment you'd like to grade
                </p>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                gap: '16px',
                alignItems: 'flex-start'
              }}
            >
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #e4b17a, #c99b69)', // $primary-gradient
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '600',
                  fontSize: '1.1rem',
                  flexShrink: 0,
                  boxShadow: '0 2px 8px rgba(228, 177, 122, 0.2)'
                }}
              >
                2
              </div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <h4
                  style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#7a5d42',
                    margin: '0 0 2px 0',
                    textAlign: 'left',
                    display: 'inline-block'
                  }}
                >
                  Fill it out with expected answers
                </h4>
                <p
                  style={{
                    color: '#8f7752', // darken($text-secondary, 10%)
                    fontSize: '0.95rem',
                    margin: '0',
                    lineHeight: '1.4',
                    textAlign: 'left'
                  }}
                >
                  No need to make it overly comprehensive, but mention the key points you'd like
                  students to hit
                </p>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                gap: '16px',
                alignItems: 'flex-start'
              }}
            >
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #e4b17a, #c99b69)', // $primary-gradient
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '600',
                  fontSize: '1.1rem',
                  flexShrink: 0,
                  boxShadow: '0 2px 8px rgba(228, 177, 122, 0.2)'
                }}
              >
                3
              </div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <h4
                  style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#7a5d42',
                    margin: '0 0 2px 0',
                    textAlign: 'left',
                    display: 'inline-block'
                  }}
                >
                  Scan and upload here
                </h4>
                <p
                  style={{
                    color: '#8f7752',
                    fontSize: '0.95rem',
                    margin: '0',
                    lineHeight: '1.4',
                    textAlign: 'left'
                  }}
                >
                  Use a scanner or phone app like Genius Scan <br />(
                  <a
                    href="https://apps.apple.com/us/app/scanner-app-genius-scan/id377672876"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#e4b17a', // $primary-color
                      textDecoration: 'none',
                      fontWeight: '500',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '3px',
                      textAlign: 'left'
                    }}
                    onMouseOver={e => {
                      e.target.style.color = '#c99b69'; // darken($primary-color, 10%)
                      e.target.style.textDecoration = 'underline';
                    }}
                    onMouseOut={e => {
                      e.target.style.color = '#e4b17a';
                      e.target.style.textDecoration = 'none';
                    }}
                  >
                    iPhone <ExternalLink size={14} />
                  </a>
                  ,{' '}
                  <a
                    href="https://play.google.com/store/apps/details?id=com.thegrizzlylabs.geniusscan.free&hl=en_US"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#e4b17a', // $primary-color
                      textDecoration: 'none',
                      fontWeight: '500',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '3px',
                      textAlign: 'left'
                    }}
                    onMouseOver={e => {
                      e.target.style.color = '#c99b69'; // darken($primary-color, 10%)
                      e.target.style.textDecoration = 'underline';
                    }}
                    onMouseOut={e => {
                      e.target.style.color = '#e4b17a';
                      e.target.style.textDecoration = 'none';
                    }}
                  >
                    Android <ExternalLink size={14} />
                  </a>
                  )
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Upload Component */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
          }}
        >
          <div
            className={`upload-box ${uploadHover ? 'hover' : ''} ${uploadStatus.withSolutions ? 'uploaded' : ''}`}
            style={{
              width: '100%',
              minHeight: '320px',
              borderRadius: '16px',
              border: `2.5px dashed ${uploadHover || iconHover ? 'rgba(228, 177, 122, 0.9)' : 'rgba(228, 177, 122, 0.7)'}`,
              backgroundColor: 'rgba(255, 251, 245, 0.8)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow:
                uploadHover || iconHover
                  ? '0 6px 12px rgba(0, 0, 0, 0.08)'
                  : '0 4px 8px rgba(0, 0, 0, 0.05)',
              transform: uploadHover || iconHover ? 'translateY(-2px)' : 'translateY(0)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={() => setIconHover(true)}
            onMouseLeave={() => setIconHover(false)}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="rubric-pdf-upload-solutions"
              accept=".pdf"
              className="hidden"
              onChange={e => handleFileChange(e, 'withSolutions')}
            />
            <label
              htmlFor="rubric-pdf-upload-solutions"
              className="upload-label"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                cursor: 'pointer'
              }}
            >
              {isUploading.withSolutions ? (
                <div className="uploading-overlay">
                  <div className="uploading-spinner"></div>
                  <span className="uploading-text">Uploading...</span>
                </div>
              ) : uploadStatus.withSolutions ? (
                <div
                  className="success-overlay"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                  }}
                >
                  <div className="success-checkmark" style={{ color: '#4caf50', fontSize: '48px' }}>
                    <CheckCircle size={48} />
                  </div>
                  <span
                    className="success-text"
                    style={{
                      fontSize: '1.2rem',
                      fontWeight: '600',
                      margin: '12px 0 4px 0',
                      color: '#4caf50',
                      textAlign: 'left'
                    }}
                  >
                    PDF Uploaded
                  </span>
                  <span className="success-subtext" style={{ color: '#888', textAlign: 'left' }}>
                    Ready for processing
                  </span>
                </div>
              ) : (
                <>
                  {/* Upload icon */}
                  <Upload
                    color={'#e4b17a'}
                    size={iconHover ? 51 : 50}
                    style={{
                      transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      transform: iconHover ? 'scale(1.2) translateY(-8px)' : 'scale(1)',
                      filter: iconHover
                        ? 'drop-shadow(0 10px 15px rgba(228, 177, 122, 0.7))'
                        : 'drop-shadow(0 2px 3px rgba(0,0,0,0.1))',
                      opacity: iconHover ? 1 : 0.9,
                      marginBottom: '12px'
                    }}
                  />
                  <span
                    style={{
                      fontSize: '1.15rem',
                      fontWeight: '600',
                      marginBottom: '12px',
                      color: '#c99b69',
                      textAlign: 'left'
                    }}
                  >
                    Upload your answer key
                  </span>
                  <p style={{ margin: '0 0 20px 0', textAlign: 'left', color: '#777' }}>
                    Click to browse or drag and drop
                  </p>
                  <div
                    style={{
                      padding: '6px 12px',
                      backgroundColor: 'rgba(228, 177, 122, 0.2)',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      color: '#c99b69',
                      fontWeight: '500',
                      textAlign: 'left'
                    }}
                  >
                    <span>PDF files only - Max 10MB</span>
                  </div>
                </>
              )}
            </label>
          </div>
        </div>
      </div>

      {/* Proceed Button - Centered on page */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: '25px',
          position: 'sticky',
          bottom: '20px',
          zIndex: 10
        }}
      >
        <button
          className={`proceed-button ${uploadStatus.withSolutions ? 'enabled' : 'disabled'}`}
          disabled={!uploadStatus.withSolutions}
          style={{
            width: '100%',
            maxWidth: '300px',
            padding: '12px 24px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            backgroundColor: uploadStatus.withSolutions ? '#e4b17a' : '#f0f0f0',
            color: uploadStatus.withSolutions ? '#fff' : '#999',
            border: 'none',
            fontWeight: '600',
            cursor: uploadStatus.withSolutions ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s ease',
            textAlign: 'left'
          }}
          onClick={() => {
            if (uploadStatus.withSolutions) {
              handleProcessSolutions();
            }
          }}
        >
          <span>Proceed</span>
          <ArrowRight />
        </button>
        {!uploadStatus.withSolutions && (
          <p style={{ textAlign: 'left', marginTop: '10px', color: '#999', fontSize: '0.9rem' }}>
            Upload solutions to proceed
          </p>
        )}
      </div>
    </div>
  );
};

export default SolutionUploadComponent;
